import { type Inspection } from './types';

/**
 * How a point, and then an inspection, rolls up to a status. The one definition every count
 * and every point-level colour in the app reads, so the viewer's panel, the inspection list,
 * the inspection page, the project overview and the checklist tab cannot disagree — they did,
 * when some read the current step and others the worst one. The roll-up is the same rule the
 * backend applies in `getInspectionStatus`, which is what the dashboard's stored statuses are.
 */

export type NoticeCounts = Record<Inspection.NoticeType, number>;

/** Worst first — the order the roll-up resolves in and the UI lists in. */
export const noticeTypesBySeverity: Inspection.NoticeType[] = ['Error', 'Warning', 'NotChecked', 'Ok'];

/**
 * The interface string each status is named by. The one definition: the names had drifted
 * apart screen by screen, an "OK" here and a "Successful" there, a "Pending" for a step nobody
 * had looked at. The words behind these keys are the SuPAR app's own for a step's status
 * (Not Checked, OK, Partly OK, Not OK): the inspector gives the status on the device under
 * those names and the app's own report prints them, so a reader meets one word in both
 * products. The backend's `noticeLabelKey` (`core/utils/report/pdf/format.ts`) names the same
 * keys for the PDF.
 */
export const noticeLabelKeys: Record<Inspection.NoticeType, string> = {
  NotChecked: 'Common_NotChecked',
  Ok: 'Common_Ok',
  Warning: 'Common_Warning',
  Error: 'Common_Error',
};

/**
 * One step's notice as everything that shows or counts it reads it: a step with no notice, or
 * one this app does not know, is unchecked. The backend's `SPInspection.stepNoticeType`,
 * mirrored.
 */
export const stepNoticeType = (step: Pick<Inspection.Step, 'noticeType'> | undefined): Inspection.NoticeType =>
  (step && noticeTypesBySeverity.includes(step.noticeType) ? step.noticeType : 'NotChecked');

/**
 * A point's status: the worst notice across **all** its steps.
 *
 * A point's steps are checked together — it is done only when every one of them is — so a
 * point whose first step is Ok and second is unchecked is unchecked, not Ok. Reading the step
 * the point currently sits at (`stepIndex`) got exactly that wrong: `stepIndex` is where the
 * inspector last was, and it stays on a finished step while a later one is still open.
 *
 * Undefined for a point with no steps at all: that is malformed data rather than an unchecked
 * point — and an empty list would otherwise count as "all steps Ok" — so callers leave it out
 * of the counts instead of reporting something the file does not say. A step carrying no
 * notice, or one this app does not know, counts as unchecked.
 */
export const poiNoticeType = (poi: Inspection.PointOfInterest): Inspection.NoticeType | undefined => {
  const steps = poi.steps ?? [];

  if (steps.length === 0) return undefined;

  const notices = new Set(steps.map(stepNoticeType));

  return noticeTypesBySeverity.find((noticeType) => notices.has(noticeType));
};

export const countPoiNotices = (pois: Inspection.PointOfInterest[] = []): NoticeCounts => {
  const counts: NoticeCounts = { Error: 0, Warning: 0, NotChecked: 0, Ok: 0 };

  pois.forEach((poi) => {
    const noticeType = poiNoticeType(poi);

    if (noticeType) counts[noticeType]++;
  });

  return counts;
};

/**
 * The single bucket an inspection falls into: the worst notice among its points. One with
 * nothing to report on — no points, or none carrying a usable step — reads as unchecked
 * rather than as fine.
 */
export const inspectionNoticeType = (pois?: Inspection.PointOfInterest[]): Inspection.NoticeType => {
  const counts = countPoiNotices(pois);

  return noticeTypesBySeverity.find((noticeType) => counts[noticeType] > 0) ?? 'NotChecked';
};
